/**
 * FridgeIQ — Product Library
 * Manage all SKUs — add, edit, delete, assign to machines.
 * Aliases auto-generated by Claude — no operator input needed.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import {
  getProducts,
  saveProduct,
  deactivateProduct,
  assignProductToMachine,
} from "../services/dynamodb";
import { uploadProductImage, getProductImageUrl } from "../services/s3";
import { generateProductAliases } from "../services/claude";

const MACHINE_IDS = (
  process.env.REACT_APP_MACHINE_IDS || "FIQ-001,FIQ-002"
).split(",");
const MACHINE_META = {
  "FIQ-001": "Southern Nutrition — Lobby",
  "FIQ-002": "Enterprise Gym — Weight Room",
};

const BASE_CATEGORIES = [
  "Energy Drink",
  "Protein Drink",
  "Snack",
  "Sports Drink",
  "Water",
];

function generateSku(name) {
  const clean = name
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase()
    .slice(0, 4);
  return `${clean}${Math.floor(Math.random() * 900 + 100)}`;
}

// ── Image Picker ──────────────────────────────────────────────────────────────
function ImagePicker({ value, onChange }) {
  const fileRef = useRef();
  const videoRef = useRef();
  const canvasRef = useRef();
  const [mode, setMode] = useState("idle");
  const [urlInput, setUrl] = useState("");
  const [stream, setStream] = useState(null);
  const [preview, setPreview] = useState(null);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      setStream(null);
    }
    setMode("idle");
  }, [stream]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const startCamera = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
      });
      setStream(s);
      setMode("camera");
      setTimeout(() => {
        if (videoRef.current) videoRef.current.srcObject = s;
      }, 100);
    } catch {
      alert("Camera not available — try uploading a file instead.");
    }
  };

  const takePhoto = () => {
    const video = videoRef.current,
      canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        const file = new File([blob], "photo.jpg", { type: "image/jpeg" });
        setPreview(URL.createObjectURL(blob));
        onChange(file, null);
        stopCamera();
      },
      "image/jpeg",
      0.9,
    );
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    onChange(file, null);
  };

  const handleUrl = () => {
    if (!urlInput.trim()) return;
    setPreview(urlInput);
    onChange(null, urlInput);
    setMode("idle");
  };

  const currentPreview = preview || value;

  return (
    <div>
      {currentPreview && (
        <div
          style={{
            width: "100%",
            height: 150,
            borderRadius: 10,
            overflow: "hidden",
            marginBottom: 10,
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={currentPreview}
            alt="Product"
            style={{
              maxHeight: 140,
              maxWidth: "100%",
              objectFit: "contain",
              padding: 8,
            }}
          />
        </div>
      )}
      {mode === "camera" && (
        <div style={{ marginBottom: 10, borderRadius: 10, overflow: "hidden" }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{ width: "100%", borderRadius: 10, display: "block" }}
          />
          <canvas ref={canvasRef} style={{ display: "none" }} />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button
              onClick={takePhoto}
              style={{
                flex: 1,
                background: "rgba(16,185,129,0.2)",
                border: "1px solid rgba(16,185,129,0.4)",
                color: "#10b981",
                borderRadius: 8,
                padding: "8px",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              📸 Take Photo
            </button>
            <button
              onClick={stopCamera}
              style={{
                flex: 1,
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#9ca3af",
                borderRadius: 8,
                padding: "8px",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {mode === "url" && (
        <div style={{ marginBottom: 10 }}>
          <input
            value={urlInput}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com/image.jpg"
            style={{
              width: "100%",
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 8,
              padding: "8px 12px",
              color: "#f9fafb",
              fontSize: 13,
              outline: "none",
              marginBottom: 6,
            }}
          />
          <div style={{ display: "flex", gap: 6 }}>
            <button
              onClick={handleUrl}
              style={{
                flex: 1,
                background: "rgba(16,185,129,0.15)",
                border: "1px solid rgba(16,185,129,0.3)",
                color: "#10b981",
                borderRadius: 7,
                padding: "6px",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              Use URL
            </button>
            <button
              onClick={() => setMode("idle")}
              style={{
                flex: 1,
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                color: "#6b7280",
                borderRadius: 7,
                padding: "6px",
                fontSize: 12,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {mode === "idle" && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 6,
          }}
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handleFile}
            style={{ display: "none" }}
          />
          <button
            onClick={() => fileRef.current.click()}
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "#9ca3af",
              borderRadius: 8,
              padding: "8px 6px",
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            📁 Upload
          </button>
          <button
            onClick={startCamera}
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "#9ca3af",
              borderRadius: 8,
              padding: "8px 6px",
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            📷 Camera
          </button>
          <button
            onClick={() => setMode("url")}
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "#9ca3af",
              borderRadius: 8,
              padding: "8px 6px",
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            🔗 URL
          </button>
        </div>
      )}
    </div>
  );
}

// ── Category Selector ─────────────────────────────────────────────────────────
function CategorySelector({ value, existingCategories, onChange }) {
  const [showCustom, setShowCustom] = useState(false);
  const [custom, setCustom] = useState("");
  const allCategories = [
    ...new Set([...BASE_CATEGORIES, ...existingCategories]),
  ].sort();

  const handleSelect = (e) => {
    if (e.target.value === "__other__") {
      setShowCustom(true);
      onChange("");
    } else {
      setShowCustom(false);
      onChange(e.target.value);
    }
  };

  const handleCustomSubmit = () => {
    const trimmed = custom.trim();
    if (!trimmed) return;
    const existing = allCategories.find(
      (c) => c.toLowerCase() === trimmed.toLowerCase(),
    );
    onChange(existing || trimmed);
    setShowCustom(false);
    setCustom("");
  };

  return (
    <div>
      <select
        value={
          showCustom
            ? "__other__"
            : allCategories.includes(value)
              ? value
              : value
                ? "__other__"
                : ""
        }
        onChange={handleSelect}
        style={{
          width: "100%",
          background: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 8,
          padding: "9px 12px",
          color: value ? "#f9fafb" : "#6b7280",
          fontSize: 13,
          outline: "none",
          marginBottom: showCustom ? 8 : 0,
        }}
      >
        <option value="" disabled>
          Select a category...
        </option>
        {allCategories.map((cat) => (
          <option key={cat} value={cat} style={{ background: "#0f1612" }}>
            {cat}
          </option>
        ))}
        <option value="__other__" style={{ background: "#0f1612" }}>
          + Other (custom)
        </option>
      </select>
      {showCustom && (
        <div style={{ display: "flex", gap: 6 }}>
          <input
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCustomSubmit()}
            placeholder="e.g. Meal Replacement"
            autoFocus
            style={{
              flex: 1,
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 8,
              padding: "8px 12px",
              color: "#f9fafb",
              fontSize: 13,
              outline: "none",
            }}
          />
          <button
            onClick={handleCustomSubmit}
            style={{
              background: "rgba(16,185,129,0.15)",
              border: "1px solid rgba(16,185,129,0.3)",
              color: "#10b981",
              borderRadius: 8,
              padding: "8px 14px",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            Add
          </button>
          <button
            onClick={() => {
              setShowCustom(false);
              setCustom("");
            }}
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "#6b7280",
              borderRadius: 8,
              padding: "8px 10px",
              fontSize: 12,
            }}
          >
            ✕
          </button>
        </div>
      )}
      {value && !showCustom && (
        <div style={{ fontSize: 11, color: "#10b981", marginTop: 4 }}>
          ✓ {value}
        </div>
      )}
    </div>
  );
}

// ── Product Modal ─────────────────────────────────────────────────────────────
function ProductModal({ product, existingCategories, onClose, onSave }) {
  const isNew = !product?.sku;
  const [form, setForm] = useState({
    sku: product?.sku || "",
    name: product?.name || "",
    brand: product?.brand || "",
    category: product?.category || "",
    price: product?.price || "",
    cost: product?.cost || "",
    tax_rate: product?.tax_rate || 0,
    image_url: product?.image_url || "",
    image_s3: product?.image_s3 || "",
    active: product?.active !== false,
    aliases: product?.aliases || [],
  });
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aliasStatus, setAliasStatus] = useState(
    product?.aliases?.length ? `${product.aliases.length} aliases` : "",
  );
  const [error, setError] = useState("");

  const handleImageChange = (file, url) => {
    if (file) setImageFile(file);
    if (url) setForm((f) => ({ ...f, image_url: url }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setError("Product name is required");
      return;
    }
    if (!form.category.trim()) {
      setError("Category is required");
      return;
    }
    if (!form.price) {
      setError("Price is required");
      return;
    }

    setSaving(true);
    setError("");

    let sku = form.sku || generateSku(form.name);
    let image_s3 = form.image_s3;
    let image_url = form.image_url;
    let aliases = form.aliases;

    if (imageFile) {
      const key = await uploadProductImage(sku, imageFile);
      if (key) {
        image_s3 = key;
        image_url = "";
      }
    }

    const needsAliases = isNew || !aliases.length;
    if (needsAliases) {
      setAliasStatus("Generating aliases with Claude...");
      aliases = await generateProductAliases(
        form.name,
        form.brand,
        form.category,
      );
      setAliasStatus(`${aliases.length} aliases generated`);
    }

    const saved = {
      ...form,
      sku,
      image_s3,
      image_url,
      aliases,
      price: parseFloat(form.price),
      cost: parseFloat(form.cost) || 0,
      tax_rate: parseFloat(form.tax_rate) || 0,
    };

    const ok = await saveProduct(saved);
    setSaving(false);
    if (ok) onSave(saved);
    else setError("Failed to save — check console");
  };

  const inputStyle = {
    width: "100%",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 8,
    padding: "9px 12px",
    color: "#f9fafb",
    fontSize: 13,
    outline: "none",
  };
  const labelStyle = {
    fontSize: 11,
    color: "#9ca3af",
    display: "block",
    marginBottom: 5,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0f1612",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 16,
          padding: 28,
          width: 580,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            fontSize: 17,
            fontWeight: 700,
            color: "#f9fafb",
            marginBottom: 4,
          }}
        >
          {isNew ? "Add New Product" : "Edit Product"}
        </div>
        <div style={{ fontSize: 12, color: "#4b5563", marginBottom: 20 }}>
          Aliases are auto-generated by Claude — no manual input needed
        </div>

        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}
        >
          <div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Product Name *</label>
              <input
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                placeholder="Gatorade Lemon Lime 20oz"
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Brand</label>
              <input
                value={form.brand}
                onChange={(e) =>
                  setForm((f) => ({ ...f, brand: e.target.value }))
                }
                placeholder="Gatorade"
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Category *</label>
              <CategorySelector
                value={form.category}
                existingCategories={existingCategories}
                onChange={(cat) => setForm((f) => ({ ...f, category: cat }))}
              />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>SKU</label>
              <input
                value={form.sku}
                onChange={(e) =>
                  setForm((f) => ({ ...f, sku: e.target.value }))
                }
                placeholder={isNew ? "Auto-generated" : ""}
                style={inputStyle}
              />
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
                marginBottom: 14,
              }}
            >
              <div>
                <label style={labelStyle}>Price *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, price: e.target.value }))
                  }
                  placeholder="3.00"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Cost</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.cost}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, cost: e.target.value }))
                  }
                  placeholder="1.50"
                  style={inputStyle}
                />
              </div>
            </div>

            <div
              style={{
                background: "rgba(16,185,129,0.06)",
                border: "1px solid rgba(16,185,129,0.15)",
                borderRadius: 8,
                padding: "10px 14px",
              }}
            >
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>
                🤖 CLAUDE ALIASES
              </div>
              <div style={{ fontSize: 12, color: "#10b981" }}>
                {aliasStatus ||
                  (isNew
                    ? "Will be auto-generated when you save"
                    : `${form.aliases.length} aliases saved`)}
              </div>
              {form.aliases.length > 0 && (
                <div
                  style={{
                    fontSize: 11,
                    color: "#4b5563",
                    marginTop: 6,
                    lineHeight: 1.6,
                  }}
                >
                  {form.aliases.slice(0, 6).join(" · ")}
                  {form.aliases.length > 6 &&
                    ` +${form.aliases.length - 6} more`}
                </div>
              )}
            </div>
          </div>

          <div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Product Image</label>
              <ImagePicker
                value={form.image_url || null}
                onChange={handleImageChange}
              />
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  onClick={() => setForm((f) => ({ ...f, active: !f.active }))}
                  style={{
                    width: 44,
                    height: 24,
                    borderRadius: 12,
                    background: form.active
                      ? "#10b981"
                      : "rgba(255,255,255,0.1)",
                    position: "relative",
                    cursor: "pointer",
                    transition: "background 0.2s",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 2,
                      left: form.active ? 22 : 2,
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      background: "white",
                      transition: "left 0.2s",
                    }}
                  />
                </div>
                <span
                  style={{
                    fontSize: 13,
                    color: form.active ? "#10b981" : "#6b7280",
                  }}
                >
                  {form.active ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div
            style={{
              color: "#f87171",
              fontSize: 12,
              marginBottom: 12,
              marginTop: 8,
            }}
          >
            ⚠️ {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "#9ca3af",
              borderRadius: 8,
              padding: "10px",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{
              flex: 2,
              background: saving
                ? "rgba(16,185,129,0.1)"
                : "rgba(16,185,129,0.2)",
              border: "1px solid rgba(16,185,129,0.4)",
              color: "#10b981",
              borderRadius: 8,
              padding: "10px",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {saving
              ? aliasStatus.includes("Generating")
                ? "🤖 Generating aliases..."
                : "Saving..."
              : isNew
                ? "Add Product"
                : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Assign Modal ──────────────────────────────────────────────────────────────
function AssignModal({ product, onClose, onAssigned }) {
  const [assignments, setAssignments] = useState(
    Object.fromEntries(
      MACHINE_IDS.map((id) => [id, { checked: false, qty: 12 }]),
    ),
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    for (const [machineId, val] of Object.entries(assignments)) {
      if (val.checked)
        await assignProductToMachine(
          machineId,
          product,
          parseInt(val.qty) || 0,
        );
    }
    setSaving(false);
    onAssigned();
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0f1612",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 16,
          padding: 28,
          width: 420,
        }}
      >
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#f9fafb",
            marginBottom: 4,
          }}
        >
          Assign to Machines
        </div>
        <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 20 }}>
          {product.name}
        </div>
        {MACHINE_IDS.map((id) => (
          <div
            key={id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: "12px 0",
              borderBottom: "1px solid rgba(255,255,255,0.05)",
            }}
          >
            <div
              onClick={() =>
                setAssignments((a) => ({
                  ...a,
                  [id]: { ...a[id], checked: !a[id].checked },
                }))
              }
              style={{
                width: 20,
                height: 20,
                borderRadius: 5,
                cursor: "pointer",
                flexShrink: 0,
                background: assignments[id].checked
                  ? "#10b981"
                  : "rgba(255,255,255,0.08)",
                border: `1px solid ${assignments[id].checked ? "#10b981" : "rgba(255,255,255,0.15)"}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {assignments[id].checked && (
                <span style={{ color: "white", fontSize: 12 }}>✓</span>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: "#f9fafb" }}>
                {MACHINE_META[id] || id}
              </div>
              <div style={{ fontSize: 11, color: "#4b5563" }}>{id}</div>
            </div>
            {assignments[id].checked && (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 11, color: "#6b7280" }}>
                  Start qty:
                </span>
                <input
                  type="number"
                  min="0"
                  value={assignments[id].qty}
                  onChange={(e) =>
                    setAssignments((a) => ({
                      ...a,
                      [id]: { ...a[id], qty: e.target.value },
                    }))
                  }
                  style={{
                    width: 60,
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 6,
                    padding: "4px 8px",
                    color: "#f9fafb",
                    fontSize: 13,
                    outline: "none",
                  }}
                />
              </div>
            )}
          </div>
        ))}
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "#9ca3af",
              borderRadius: 8,
              padding: "10px",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              flex: 1,
              background: "rgba(16,185,129,0.2)",
              border: "1px solid rgba(16,185,129,0.4)",
              color: "#10b981",
              borderRadius: 8,
              padding: "10px",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {saving ? "Assigning..." : "Assign"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Delete Modal ──────────────────────────────────────────────────────────────
function DeleteModal({ product, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    setDeleting(true);
    await deactivateProduct(product.sku);
    setDeleting(false);
    onDeleted();
  };
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0f1612",
          border: "1px solid rgba(239,68,68,0.3)",
          borderRadius: 16,
          padding: 28,
          width: 380,
        }}
      >
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#f9fafb",
            marginBottom: 8,
          }}
        >
          Delete Product
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#9ca3af",
            marginBottom: 20,
            lineHeight: 1.5,
          }}
        >
          Are you sure you want to delete{" "}
          <strong style={{ color: "#f9fafb" }}>{product.name}</strong>? This
          will deactivate it and remove it from Claude's recognition. Existing
          transactions are unaffected.
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "#9ca3af",
              borderRadius: 8,
              padding: "10px",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            style={{
              flex: 1,
              background: "rgba(239,68,68,0.2)",
              border: "1px solid rgba(239,68,68,0.4)",
              color: "#f87171",
              borderRadius: 8,
              padding: "10px",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Category Manager Modal ────────────────────────────────────────────────────
function CategoryManagerModal({
  products,
  existingCategories,
  onClose,
  onSaved,
}) {
  const customCategories = existingCategories.filter(
    (c) => !BASE_CATEGORIES.includes(c),
  );
  const [reassignMap, setReassignMap] = useState(
    Object.fromEntries(customCategories.map((c) => [c, BASE_CATEGORIES[0]])),
  );
  const [deleting, setDeleting] = useState(null);
  const [deleted, setDeleted] = useState(new Set());

  const productCountFor = (cat) =>
    products.filter((p) => p.category === cat).length;

  const handleDelete = async (cat) => {
    const fallback = reassignMap[cat];
    setDeleting(cat);
    const affected = products.filter((p) => p.category === cat);
    await Promise.all(
      affected.map((p) => saveProduct({ ...p, category: fallback })),
    );
    setDeleted((prev) => new Set([...prev, cat]));
    setDeleting(null);
    onSaved();
  };

  const visibleCustom = customCategories.filter((c) => !deleted.has(c));

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0f1612",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 16,
          padding: 28,
          width: 500,
          maxHeight: "80vh",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            fontSize: 17,
            fontWeight: 700,
            color: "#f9fafb",
            marginBottom: 4,
          }}
        >
          Manage Categories
        </div>
        <div style={{ fontSize: 12, color: "#4b5563", marginBottom: 20 }}>
          Built-in categories are protected. Custom categories can be merged
          into another on deletion.
        </div>

        {/* Built-in — read only */}
        <div
          style={{
            fontSize: 11,
            color: "#6b7280",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            marginBottom: 8,
          }}
        >
          Built-in
        </div>
        {BASE_CATEGORIES.map((cat) => (
          <div
            key={cat}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "9px 12px",
              marginBottom: 4,
              background: "rgba(255,255,255,0.02)",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.05)",
            }}
          >
            <span style={{ fontSize: 13, color: "#9ca3af" }}>{cat}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 11, color: "#4b5563" }}>
                {productCountFor(cat)} product
                {productCountFor(cat) !== 1 ? "s" : ""}
              </span>
              <span
                style={{
                  fontSize: 10,
                  color: "#4b5563",
                  background: "rgba(255,255,255,0.04)",
                  borderRadius: 5,
                  padding: "2px 8px",
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                Built-in
              </span>
            </div>
          </div>
        ))}

        {/* Custom — deletable */}
        {visibleCustom.length > 0 && (
          <>
            <div
              style={{
                fontSize: 11,
                color: "#6b7280",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                margin: "16px 0 8px",
              }}
            >
              Custom
            </div>
            {visibleCustom.map((cat) => {
              const count = productCountFor(cat);
              const isDeleting = deleting === cat;
              const fallbackOptions = [
                ...BASE_CATEGORIES,
                ...visibleCustom.filter((c) => c !== cat && !deleted.has(c)),
              ];

              return (
                <div
                  key={cat}
                  style={{
                    padding: 12,
                    marginBottom: 8,
                    background: "rgba(239,68,68,0.03)",
                    borderRadius: 8,
                    border: "1px solid rgba(239,68,68,0.1)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: count > 0 ? 10 : 8,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        color: "#f9fafb",
                        fontWeight: 600,
                      }}
                    >
                      {cat}
                    </span>
                    <span style={{ fontSize: 11, color: "#6b7280" }}>
                      {count} product{count !== 1 ? "s" : ""}
                    </span>
                  </div>

                  {count > 0 && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 10,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          color: "#6b7280",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Reassign {count} product{count !== 1 ? "s" : ""} to:
                      </span>
                      <select
                        value={reassignMap[cat] || BASE_CATEGORIES[0]}
                        onChange={(e) =>
                          setReassignMap((m) => ({
                            ...m,
                            [cat]: e.target.value,
                          }))
                        }
                        style={{
                          flex: 1,
                          background: "rgba(255,255,255,0.05)",
                          border: "1px solid rgba(255,255,255,0.1)",
                          borderRadius: 6,
                          padding: "5px 10px",
                          color: "#f9fafb",
                          fontSize: 12,
                          outline: "none",
                        }}
                      >
                        {fallbackOptions.map((o) => (
                          <option
                            key={o}
                            value={o}
                            style={{ background: "#0f1612" }}
                          >
                            {o}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <button
                    disabled={isDeleting}
                    onClick={() => {
                      const msg =
                        count > 0
                          ? `Delete "${cat}" and move ${count} product(s) to "${reassignMap[cat] || BASE_CATEGORIES[0]}"?`
                          : `Delete empty category "${cat}"?`;
                      if (window.confirm(msg)) handleDelete(cat);
                    }}
                    style={{
                      width: "100%",
                      background: isDeleting
                        ? "rgba(239,68,68,0.05)"
                        : "rgba(239,68,68,0.12)",
                      border: "1px solid rgba(239,68,68,0.25)",
                      color: isDeleting ? "#6b7280" : "#f87171",
                      borderRadius: 7,
                      padding: "7px",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {isDeleting
                      ? "Deleting & reassigning…"
                      : `🗑 Delete "${cat}"`}
                  </button>
                </div>
              );
            })}
          </>
        )}

        {visibleCustom.length === 0 && (
          <div
            style={{
              padding: "20px 0",
              textAlign: "center",
              color: "#4b5563",
              fontSize: 13,
            }}
          >
            No custom categories to manage.
          </div>
        )}

        <button
          onClick={onClose}
          style={{
            width: "100%",
            marginTop: 16,
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "#9ca3af",
            borderRadius: 8,
            padding: "10px",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Done
        </button>
      </div>
    </div>
  );
}

// ── Product Card ──────────────────────────────────────────────────────────────
function ProductCard({
  product,
  onEdit,
  onAssign,
  onDelete,
  onRegenerateAliases,
}) {
  const [imgUrl, setImgUrl] = useState(product.image_url || null);
  const [regenerating, setRegen] = useState(false);

  useEffect(() => {
    if (product.image_s3 && !product.image_url) {
      getProductImageUrl(product.image_s3).then((url) => {
        if (url) setImgUrl(url);
      });
    }
  }, [product]);

  const handleRegenerate = async (e) => {
    e.stopPropagation();
    setRegen(true);
    await onRegenerateAliases(product);
    setRegen(false);
  };

  return (
    <div
      style={{
        background: "rgba(255,255,255,0.02)",
        border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: 12,
        overflow: "hidden",
        opacity: product.active === false ? 0.5 : 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          height: 130,
          background: "rgba(255,255,255,0.03)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderBottom: "1px solid rgba(255,255,255,0.05)",
          flexShrink: 0,
        }}
      >
        {imgUrl ? (
          <img
            src={imgUrl}
            alt={product.name}
            style={{
              maxHeight: 120,
              maxWidth: "100%",
              objectFit: "contain",
              padding: 8,
            }}
          />
        ) : (
          <div style={{ fontSize: 36, opacity: 0.2 }}>📦</div>
        )}
      </div>

      <div
        style={{
          padding: "12px 14px",
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "#f9fafb",
            marginBottom: 2,
            lineHeight: 1.3,
          }}
        >
          {product.name}
        </div>
        <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>
          {product.category}
          {product.brand && ` · ${product.brand}`}
        </div>

        <div
          style={{
            fontSize: 11,
            color: "#4b5563",
            marginBottom: 8,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          🤖 {product.aliases?.length || 0} aliases
          {!product.aliases?.length && (
            <span style={{ color: "#f59e0b", fontSize: 10 }}>⚠️ none</span>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <span style={{ fontSize: 18, fontWeight: 700, color: "#10b981" }}>
            ${parseFloat(product.price || 0).toFixed(2)}
          </span>
          {product.active === false && (
            <span
              style={{
                background: "rgba(239,68,68,0.1)",
                border: "1px solid rgba(239,68,68,0.2)",
                color: "#f87171",
                borderRadius: 6,
                padding: "2px 8px",
                fontSize: 10,
                fontWeight: 600,
              }}
            >
              Inactive
            </span>
          )}
        </div>

        <div
          style={{
            marginTop: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 5,
          }}
        >
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}
          >
            <button
              onClick={() => onEdit(product)}
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.08)",
                color: "#9ca3af",
                borderRadius: 7,
                padding: "6px",
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              ✏️ Edit
            </button>
            <button
              onClick={() => onAssign(product)}
              style={{
                background: "rgba(96,165,250,0.1)",
                border: "1px solid rgba(96,165,250,0.25)",
                color: "#60a5fa",
                borderRadius: 7,
                padding: "6px",
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              🏪 Assign
            </button>
          </div>
          <button
            onClick={handleRegenerate}
            disabled={regenerating}
            style={{
              width: "100%",
              background: "rgba(16,185,129,0.06)",
              border: "1px solid rgba(16,185,129,0.15)",
              color: regenerating ? "#4b5563" : "#10b981",
              borderRadius: 7,
              padding: "5px",
              fontSize: 11,
            }}
          >
            {regenerating ? "🤖 Generating..." : "🤖 Regenerate Aliases"}
          </button>
          <button
            onClick={() => onDelete(product)}
            style={{
              width: "100%",
              background: "rgba(239,68,68,0.06)",
              border: "1px solid rgba(239,68,68,0.12)",
              color: "#f87171",
              borderRadius: 7,
              padding: "5px",
              fontSize: 11,
            }}
          >
            🗑 Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function ProductLibrary() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editProduct, setEdit] = useState(null);
  const [assignProduct, setAssign] = useState(null);
  const [deleteProduct, setDelete] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCatManager, setShowCatManager] = useState(false);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await getProducts();
    setProducts(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const existingCategories = [
    ...new Set(products.map((p) => p.category).filter(Boolean)),
  ];
  const allCategories = [
    "all",
    ...new Set([...BASE_CATEGORIES, ...existingCategories]),
  ].sort();

  const filtered = products
    .filter((p) => {
      if (!showInactive && p.active === false) return false;
      if (catFilter !== "all" && p.category !== catFilter) return false;
      if (
        search &&
        !p.name.toLowerCase().includes(search.toLowerCase()) &&
        !p.category?.toLowerCase().includes(search.toLowerCase()) &&
        !p.brand?.toLowerCase().includes(search.toLowerCase())
      )
        return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === "price") return (a.price || 0) - (b.price || 0);
      if (sortBy === "category")
        return (a.category || "").localeCompare(b.category || "");
      return (a.name || "").localeCompare(b.name || "");
    });

  const handleRegenerateAliases = async (product) => {
    const aliases = await generateProductAliases(
      product.name,
      product.brand,
      product.category,
    );
    if (aliases.length) {
      await saveProduct({ ...product, aliases });
      load();
    }
  };

  const activeCount = products.filter((p) => p.active !== false).length;
  const inactiveCount = products.filter((p) => p.active === false).length;
  const noAliasCount = products.filter(
    (p) => p.active !== false && !p.aliases?.length,
  ).length;

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#f9fafb" }}>
            Product Library
          </div>
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            {activeCount} active
            {inactiveCount > 0 && `, ${inactiveCount} inactive`}
            {noAliasCount > 0 && (
              <span style={{ color: "#f59e0b", marginLeft: 8 }}>
                ⚠️ {noAliasCount} missing aliases
              </span>
            )}
          </div>
        </div>
        <button
          onClick={() => {
            setEdit(null);
            setShowModal(true);
          }}
          style={{
            background: "rgba(16,185,129,0.2)",
            border: "1px solid rgba(16,185,129,0.4)",
            color: "#10b981",
            borderRadius: 10,
            padding: "10px 20px",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          + Add Product
        </button>
      </div>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 6,
          marginBottom: 20,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {allCategories.map((cat) => (
          <button
            key={cat}
            onClick={() => setCatFilter(cat)}
            style={{
              background:
                catFilter === cat
                  ? "rgba(16,185,129,0.15)"
                  : "rgba(255,255,255,0.04)",
              border: `1px solid ${catFilter === cat ? "rgba(16,185,129,0.35)" : "rgba(255,255,255,0.08)"}`,
              color: catFilter === cat ? "#10b981" : "#6b7280",
              borderRadius: 8,
              padding: "5px 12px",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {cat === "all" ? `All (${activeCount})` : cat}
          </button>
        ))}

        {/* ── Manage Categories button ── */}
        <button
          onClick={() => setShowCatManager(true)}
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            color: "#6b7280",
            borderRadius: 8,
            padding: "5px 12px",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          ⚙️ Manage
        </button>

        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            gap: 8,
            alignItems: "center",
          }}
        >
          <button
            onClick={() => setShowInactive(!showInactive)}
            style={{
              background: showInactive
                ? "rgba(239,68,68,0.12)"
                : "rgba(255,255,255,0.04)",
              border: `1px solid ${showInactive ? "rgba(239,68,68,0.3)" : "rgba(255,255,255,0.08)"}`,
              color: showInactive ? "#f87171" : "#4b5563",
              borderRadius: 8,
              padding: "5px 12px",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {showInactive ? "Hide Inactive" : "Show Inactive"}
          </button>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "#9ca3af",
              borderRadius: 8,
              padding: "5px 12px",
              fontSize: 12,
              outline: "none",
            }}
          >
            <option value="name">Name</option>
            <option value="category">Category</option>
            <option value="price">Price</option>
          </select>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products..."
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 8,
              padding: "5px 14px",
              color: "#f9fafb",
              fontSize: 12,
              outline: "none",
              width: 180,
            }}
          />
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#4b5563" }}>
          Loading products...
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 60, textAlign: "center", color: "#4b5563" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📦</div>
          <div style={{ fontSize: 15, marginBottom: 6 }}>No products yet</div>
          <div style={{ fontSize: 13 }}>Click "Add Product" to get started</div>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
            gap: 14,
          }}
        >
          {filtered.map((p) => (
            <ProductCard
              key={p.sku}
              product={p}
              onEdit={(p) => {
                setEdit(p);
                setShowModal(true);
              }}
              onAssign={(p) => setAssign(p)}
              onDelete={(p) => setDelete(p)}
              onRegenerateAliases={handleRegenerateAliases}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showModal && (
        <ProductModal
          product={editProduct}
          existingCategories={existingCategories}
          onClose={() => {
            setShowModal(false);
            setEdit(null);
          }}
          onSave={() => {
            setShowModal(false);
            setEdit(null);
            load();
          }}
        />
      )}
      {assignProduct && (
        <AssignModal
          product={assignProduct}
          onClose={() => setAssign(null)}
          onAssigned={() => {
            setAssign(null);
            load();
          }}
        />
      )}
      {deleteProduct && (
        <DeleteModal
          product={deleteProduct}
          onClose={() => setDelete(null)}
          onDeleted={() => {
            setDelete(null);
            load();
          }}
        />
      )}
      {showCatManager && (
        <CategoryManagerModal
          products={products}
          existingCategories={existingCategories}
          onClose={() => setShowCatManager(false)}
          onSaved={() => {
            load();
          }}
        />
      )}
    </div>
  );
}
